import { useState, useEffect, useContext } from "react";
import ListConsentimentos from "../../Lists/ListConsentimentos/ListConsentimentos";
import "./ConsentimentosComponent.scss";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import { Popconfirm } from "antd";
import { AuthContext } from "../../../../../../shared/context/auth-context";
import { ClinicaContext } from "../../../../../../shared/context/clinica-context";
import { dadosImpressao } from "../../../../../../shared/util/dadosImpressao";
import { useLoading } from "../../../../../../shared/context/LoadingContext";
import toast from "react-hot-toast";

const TIPOS_CONSENTIMENTO = [
  {
    chave: "sms_lembretes",
    label: "Envio de SMS com lembretes de consultas e tratamentos",
  },
  {
    chave: "email_lembretes",
    label: "Envio de emails com lembretes de consultas e tratamentos",
  },
  {
    chave: "email_promocional",
    label:
      "Envio de emails com informações promocionais e campanhas da clínica",
  },
  {
    chave: "sms_promocional",
    label: "Envio de SMS com informações promocionais e campanhas da clínica",
  },
  {
    chave: "partilha_documentos",
    label: "Envio de documentos clínicos e relatórios por email",
  },
];

function ConsentimentosComponent({
  clienteId,
  sendRequest,
  dadosConsentimento,
  dadosTcle,
  open,
  handleCloseModal,
}) {
  const { startLoading, stopLoading } = useLoading();
  const auth = useContext(AuthContext);
  const clinica = useContext(ClinicaContext);

  const [consentimentos, setConsentimentos] = useState([]);
  const [selecionados, setSelecionados] = useState([]);
  const [erro, setErro] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Separação por tipo
  const consentimentosComunicacao = consentimentos.filter(
    (c) => c.tipo !== "tcle",
  );
  const consentimentosTcle = consentimentos.filter((c) => c.tipo === "tcle");

  useEffect(() => {
    const fetchConsentimentos = async () => {
      startLoading();
      try {
        const responseData = await sendRequest(
          `${process.env.REACT_APP_BACKEND_LINK}/api/consentimentos/cliente/${clienteId}`,
          "GET",
          null,
          { Authorization: "Bearer " + auth.token },
        );
        const sorted = responseData.consentimentos.sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at),
        );

        setConsentimentos(sorted);
      } catch (err) {
        console.error("err", err);
      } finally {
        stopLoading();
      }
    };
    fetchConsentimentos();
  }, [sendRequest, clienteId, auth.token]);

  const handleToggle = (chave) => {
    setSelecionados((prev) =>
      prev.includes(chave) ? prev.filter((c) => c !== chave) : [...prev, chave],
    );
  };

  const handleDesativar = async (id) => {
    startLoading();
    try {
      const formData = new FormData();
      formData.append("utilizador", auth.userId);
      await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/consentimentos/desativar/${id}`,
        "PATCH",
        formData,
        { Authorization: "Bearer " + auth.token },
      );
      setConsentimentos((prev) => prev.filter((el) => el.id !== id));
      toast.success("Consentimento desativado com sucesso!");
    } catch (err) {
      console.error("err", err);
    } finally {
      stopLoading();
    }
  };

  const verPdfConsentimento = (id) => {
    const found = consentimentos.find((c) => c.id === id);
    // A morada e os contactos impressos são os da unidade onde o documento foi
    // emitido, não os de quem o reimprime. O TCLE aqui ao lado já o fazia; este
    // passava o registo cru e saía sempre com a morada da clínica.
    if (found)
      dadosConsentimento({
        ...found,
        clinica: dadosImpressao(found.clinica, found.estabelecimento),
      });
  };

  // Constrói os dados que o PdfTcle consome a partir de um registo de TCLE.
  // O médico do documento é quem o gerou (criadoPor) — mas só se for
  // Médico/Administrador ou Dentista. Para outros perfis (ex: secretária),
  // o campo de médico fica vazio.
  const construirTcleData = (registo) => {
    const criador = registo.criadoPor;
    const roleCriador = criador?.role?.role;
    const criadorEhMedico =
      roleCriador === "Médico/Administrador" || roleCriador === "Dentista";

    return {
      cliente: {
        nome: registo.cliente?.nome || "",
        cni: registo.cliente?.identificacao || "",
      },
      // A morada impressa é a da unidade onde o documento foi emitido, não a de
      // quem o está a reimprimir.
      clinica: dadosImpressao(registo.clinica, registo.estabelecimento),
      medico: criadorEhMedico
        ? {
            name: criador.name,
            cip: criador.cip || "",
            assinatura: criador.assinatura,
          }
        : {},
      data_tcle: registo.created_at,
    };
  };

  const verPdfTcle = (id) => {
    const found = consentimentosTcle.find((c) => c.id === id);
    if (found) dadosTcle(construirTcleData(found));
  };

  const handleSubmit = async () => {
    if (selecionados.length === 0) {
      setErro("Selecione pelo menos um tipo de consentimento.");
      return;
    }
    setErro("");
    setIsSubmitting(true);
    startLoading();
    try {
      const formData = new FormData();
      formData.append("criadoPor", auth.userId);
      formData.append("cliente", clienteId);
      formData.append("clinica", clinica.clinica._id);
      formData.append("tipo", "comunicacao");
      selecionados.forEach((c) => formData.append("consentimentos[]", c));

      const resposta = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/consentimentos`,
        "POST",
        formData,
        { Authorization: "Bearer " + auth.token },
      );

      const novo = resposta.consentimento;
      setConsentimentos((prev) =>
        [novo, ...prev].sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at),
        ),
      );

      setSelecionados([]);
      setIsSubmitting(false);
      handleCloseModal();
      // Como no `verPdfConsentimento`: a morada é a da unidade do documento.
      dadosConsentimento({
        ...novo,
        clinica: dadosImpressao(novo.clinica, novo.estabelecimento),
      });
      toast.success("Consentimento gerado com sucesso!");
    } catch (err) {
      console.error(err);
    } finally {
      stopLoading();
    }
  };

  const handleGerarTcle = async () => {
    startLoading();
    try {
      const formData = new FormData();
      formData.append("criadoPor", auth.userId);
      formData.append("cliente", clienteId);
      formData.append("clinica", clinica.clinica._id);
      formData.append("tipo", "tcle");
      formData.append("consentimentos[]", "tcle");

      const resposta = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/consentimentos`,
        "POST",
        formData,
        { Authorization: "Bearer " + auth.token },
      );

      const novo = resposta.consentimento;
      setConsentimentos((prev) =>
        [novo, ...prev].sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at),
        ),
      );

      dadosTcle(construirTcleData(novo));
      toast.success("TCLE gerado com sucesso!");
    } catch (err) {
      console.error(err);
    } finally {
      stopLoading();
    }
  };

  return (
    <>
      {/* Divisão 1 — Consentimentos de comunicação / marketing */}
      <div className="consentimentos-divisao">
        <h3 className="consentimentos-divisao__titulo">
          Consentimentos de Comunicação e Marketing
        </h3>
        {consentimentosComunicacao.length > 0 ? (
          <ListConsentimentos
            data={consentimentosComunicacao}
            handleDesativar={handleDesativar}
            verPdfConsentimento={verPdfConsentimento}
          />
        ) : (
          <div className="no_data_div">
            Nenhum consentimento de comunicação encontrado.
          </div>
        )}
      </div>

      {/* Divisão 2 — Outros consentimentos (TCLE, ...) */}
      <div className="consentimentos-divisao">
        <div className="consentimentos-divisao__header">
          <h3 className="consentimentos-divisao__titulo">
            Outros Consentimentos
          </h3>
          <Popconfirm
            title="Gerar TCLE"
            description="Pretende gerar um Termo de Consentimento Livre Esclarecido (TCLE) para este cliente?"
            okText="Sim"
            cancelText="Não"
            onConfirm={handleGerarTcle}
          >
            <span className="tabs-container-top-button">Gerar TCLE</span>
          </Popconfirm>
        </div>
        {consentimentosTcle.length > 0 ? (
          <ListConsentimentos
            data={consentimentosTcle}
            handleDesativar={handleDesativar}
            verPdfConsentimento={verPdfTcle}
          />
        ) : (
          <div className="no_data_div">
            Nenhum outro consentimento encontrado.
          </div>
        )}
      </div>

      {open && (
        <Modal
          open={open}
          onClose={handleCloseModal}
          aria-labelledby="modal-consentimento"
        >
          <Box
            className="fluxo-atendimento__modal__box aprovar-orcamento__modal"
            id="modal__cinquentaPorCento"
          >
            <h1 className="fluxo-atendimento__modal__titulo">
              Gerar Declaração de Consentimento
            </h1>

            <div className="consentimento-modal__body">
              <p className="consentimento-modal__instrucao">
                Selecione os tipos de comunicação para os quais o paciente dá
                consentimento:
              </p>

              <div className="consentimento-modal__checkboxes">
                {TIPOS_CONSENTIMENTO.map(({ chave, label }) => (
                  <FormControlLabel
                    key={chave}
                    control={
                      <Checkbox
                        checked={selecionados.includes(chave)}
                        onChange={() => handleToggle(chave)}
                      />
                    }
                    label={label}
                  />
                ))}
              </div>

              {erro && (
                <div style={{ color: "crimson", marginTop: "8px" }}>{erro}</div>
              )}
            </div>

            <div className="fluxo-atendimento__modal__bottom_btns">
              <span
                className="cancel-btn"
                onClick={handleCloseModal}
                style={{ display: "block" }}
              >
                Fechar
              </span>
              <span
                className="blue-button"
                onClick={handleSubmit}
                style={{ display: "block" }}
              >
                {isSubmitting ? "Gerando..." : "Gerar"}
              </span>
            </div>
          </Box>
        </Modal>
      )}
    </>
  );
}

export default ConsentimentosComponent;
